import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import styled from 'styled-components'
import { useFegliCalculations } from '../../dashboard/hooks/useFegliCalculations'
import {
  TableContainer,
  TableRow,
  Table,
  TableBody,
  TableCell,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import Card from 'atoms/Card'
import { selectData } from 'layouts/onboarding'
import { Skeleton } from '@mui/material'
import zIndex from '@mui/material/styles/zIndex'
import fegliLogo from 'assets/images/fegliLogo.png'

// TODO: MAKE THIS LIVE DATA AND UPLOAD IMAGES

function createData(
  name,
  providerLogo,
  logoHeight,
  coverage,
  premium,
  ageCoverage
) {
  return {
    name,
    providerLogo,
    logoHeight,
    coverage,
    premium,
    ageCoverage,
  }
}

const StyledProviderLogo = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  min-height: 12px;
  max-height: ${({ isUnlocked }) => (isUnlocked ? '28px' : '22px')};
`

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const HeaderRow = () => (
  <TableRow
    sx={{
      cursor: 'default',
      transition: 'background-color 0.2s ease-in-out',
    }}
  >
    <TableCell colSpan={1} />
    <TableCell colSpan={1} sx={{ textAlign: 'left' }}>
      <Typography variant="body1Medium" color="#A3A7AA">
        Provider
      </Typography>
    </TableCell>
    <TableCell colSpan={1} sx={{ textAlign: 'right' }}>
      <Typography variant="body1Medium" color="#A3A7AA">
        Coverage
      </Typography>
    </TableCell>
    <TableCell colSpan={1} sx={{ textAlign: 'right', paddingRight: '24px' }}>
      <Typography variant="body1Medium" color="#A3A7AA">
        Cost
      </Typography>
    </TableCell>
  </TableRow>
)

const DemoRow = ({
  label,
  hasImage = true,
  image = 'https://upload.wikimedia.org/wikipedia/commons/c/c6/MetLife_logo.svg',
}) => (
  <TableRow
    sx={{
      '&:last-of-type td': {
        borderTop: '2px solid #ddd',
      },
    }}
  >
    <TableCell
      colSpan={1}
      size="large"
      sx={{
        paddingLeft: '24px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <Typography variant="body1Medium">{label}</Typography>
    </TableCell>
    <TableCell colSpan={1} sx={{ textAlign: 'left' }}>
      {hasImage && (
        <img
          style={{
            objectFit: 'contain',
            width: '70px',
            height: 'auto',
            maxHeight: '28px',
            display: 'block',
          }}
          src={image}
          alt=""
        />
      )}
    </TableCell>
    <TableCell colSpan={1} sx={{ textAlign: 'right' }}>
      <Typography textAlign="right" variant="body2">
        $XX,XXX
      </Typography>
    </TableCell>
    <TableCell colSpan={1} sx={{ textAlign: 'right', paddingRight: '24px' }}>
      <Typography textAlign="right" variant="body2">
        $XX
      </Typography>
    </TableCell>
  </TableRow>
)

function LifeInsuranceSummary({
  isDemo = false,
  isUnlocked = false,
  adminView = false,
}) {
  const InsuranceRow = ({
    row,
    index,
    isActive,
    hoveredRow,
    setHoveredRow,
  }) => {
    const getRowColor = () => {
      if (hoveredRow === index) return isActive ? '#f1f1f1' : '#f9f9f9'
      return isActive ? '#f1f1f1' : 'inherit'
    }

    return (
      <TableRow
        onMouseEnter={() => setHoveredRow(index)}
        onMouseLeave={() => setHoveredRow(null)}
        sx={{
          backgroundColor: getRowColor(),
          cursor: 'default',
          transition: 'background-color 0.2s ease-in-out',
          '&:last-of-type td': {
            borderTop: '2px solid #ddd',
          },
          '&:last-child td, &:last-child th': { borderBottom: 0 },
        }}
      >
        <TableCell
          colSpan={1}
          sx={{
            paddingLeft: isUnlocked ? '8px' : '24px',
            maxWidth: '180px',

            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography
            variant="body1Medium"
            noWrap
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              whiteSpace: 'nowrap',
            }}
          >
            {row.name}
          </Typography>
        </TableCell>

        <TableCell
          colSpan={1}
          sx={{
            textAlign: 'left',
            verticalAlign: 'middle',
            width: '80px',
            paddingX: '8px',
            '@media (max-width:500px)': {
              width: '40px',
              paddingX: '0px',
            },
          }}
        >
          <StyledProviderLogo
            isUnlocked={isUnlocked}
            src={row.providerLogo}
            // alt={row.name}
          />
        </TableCell>

        <TableCell colSpan={1} sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="#495057">
            {row.coverage}
          </Typography>
        </TableCell>
        <TableCell
          colSpan={1}
          sx={{
            textAlign: 'right',
            paddingRight: '24px',
          }}
        >
          <Typography variant="body2" color="#495057">
            {`${row.premium}`}
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  const [tableTimeHorizon, setTableTimeHorizon] = useState('monthly')
  const switchTimeHorizon = (event, newTimeHorizon) => {
    newTimeHorizon === null
      ? setTableTimeHorizon('monthly')
      : setTableTimeHorizon(newTimeHorizon)
  }
  const {
    bedb,
    basicLife,
    optionA,
    optionB,
    optionC,
    fegliTotal,
    privateInsurance,
    allLifeInsuranceTotal,
  } = useFegliCalculations()

  const [open, setOpen] = useState([])
  const [hoveredRow, setHoveredRow] = useState(null)
  const [hovered, setHovered] = useState(false)

  const isActive = (index) => open.includes(index)

  const timeFactor = tableTimeHorizon === 'monthly' ? 1 : 12

  const fegliRows = []
  const privateRows = []
  const allRows = []

  const bedbRow =
    bedb &&
    allRows.push(
      createData(
        'Basic Employee Death Benefit',
        'https://www.opm.gov/cdn/img/opm-uswds/opm_logo.svg',
        '36px',
        dollarFormat.format(bedb),
        '$0',
        '120'
      )
    )
  const basicLifeRow =
    basicLife &&
    allRows.push(
      createData(
        'Basic Life',
        fegliLogo,
        '24px',
        dollarFormat.format(basicLife?.coverage),
        dollarFormat.format(basicLife?.premium * timeFactor),
        '120'
      )
    )
  const optionARow =
    optionA &&
    allRows.push(
      createData(
        'Option A',
        fegliLogo,
        '24px',
        dollarFormat.format(optionA?.coverage),
        dollarFormat.format(optionA?.premium * timeFactor),
        '120'
      )
    )
  const optionBRow =
    optionB &&
    allRows.push(
      createData(
        'Option B',
        fegliLogo,
        '24px',
        dollarFormat.format(optionB?.coverage),
        dollarFormat.format(optionB?.premium * timeFactor),
        '120'
      )
    )
  // const optionCRow =
  //   optionC &&
  //   allRows.push(
  //     createData(
  //       'Option C',
  //       fegliLogo,
  //       '24px',
  //       dollarFormat.format(optionC.coverage),
  //       dollarFormat.format(optionC.premium),
  //       '120'
  //     )
  //   )
  // allRows.push(
  //   createData(
  //     'FEGLI Total',
  //     '',
  //     '',
  //     dollarFormat.format(fegliTotal.coverage),
  //     dollarFormat.format(fegliTotal.premium * timeFactor),
  //     ''
  //   )
  // )
  const privateInsuranceRows = privateInsurance.map((policy) => {
    const providerInfo = selectData.privateLifeInsuranceProviders.find(
      (p) => p.value === policy.provider
    )
    const typeInfo = selectData.privateLifeInsuranceTypes.find(
      (t) => t.value === policy.type
    )

    const providerLabel = providerInfo?.label || 'Not Selected'
    const providerLogo = providerInfo?.logo || ''
    const typeLabel = typeInfo?.label || 'Not Selected'

    return createData(
      `${typeLabel}`,
      providerLogo,
      '32px',
      dollarFormat.format(policy.coverage),
      dollarFormat.format(policy.premium * timeFactor),
      ''
    )
  })

  allRows.push(...privateInsuranceRows)

  // allRows.push(
  //   createData(
  //     'Private Insurance Total',
  //     '',
  //     '',
  //     dollarFormat.format(allLifeInsuranceTotal?.privateCoverage),
  //     dollarFormat.format(allLifeInsuranceTotal?.privatePremium * timeFactor),
  //     ''
  //   )
  // )

  allRows.push(
    createData(
      'Total',
      '',
      '',
      dollarFormat.format(allLifeInsuranceTotal?.coverage),
      dollarFormat.format(allLifeInsuranceTotal?.premium * timeFactor),
      ''
    )
  )

  if (isDemo) {
    return (
      <Card>
        <TableContainer sx={{ margin: 'auto', width: '100%' }}>
          <Table aria-label="life-insurance-demo-table">
            <TableBody>
              <HeaderRow />
              <DemoRow image={fegliLogo} label="Basic Life" />
              <DemoRow image={fegliLogo} label="Option B" />
              <DemoRow image={'none'} label="Private L.I." />
              <DemoRow hasImage={false} label="Total" />
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    )
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeInOut' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        {adminView ? (
          <Typography variant="h5">Life Insurance Summary</Typography>
        ) : (
          <Typography
            display={'flex'}
            align={'left'}
            variant={'body2Fun'}
            color={'#495057'}
            letterSpacing={0.32}
            my={hovered ? 0 : '9px'}
            fontSize={'18px !important'}
          >
            LIFE INSURANCE SUMMARY
          </Typography>
        )}
        <AnimatePresence>
          {(hovered || adminView) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <ToggleButtonGroup
                value={tableTimeHorizon}
                exclusive
                onChange={switchTimeHorizon}
                aria-label="TimeHorizon"
                sx={{
                  height: '36px',
                  alignSelf: 'center',
                  borderRadius: '36px',
                  background: '#A3A7AA',
                  border: '3px solid #A3A7AA',
                }}
              >
                <ToggleButton
                  sx={{
                    borderRadius: '36px !important',
                    border: 'none',
                    color: '#fff',
                    textTransform: 'capitalize',
                    fontWeight: '500',
                    letterSpacing: '0.5px',
                    '&.Mui-selected': {
                      backgroundColor: '#fff !important',
                      color: '#75787B',
                      fontWeight: '600',
                    },
                  }}
                  value="monthly"
                >
                  Monthly
                </ToggleButton>
                <ToggleButton
                  sx={{
                    borderRadius: '36px !important',
                    border: 'none',
                    color: '#fff',
                    textTransform: 'capitalize',
                    fontWeight: '500',
                    letterSpacing: '0.5px',
                    '&.Mui-selected': {
                      backgroundColor: '#fff !important',
                      color: '#75787B',
                      fontWeight: '600',
                    },
                  }}
                  value="yearly"
                >
                  Yearly
                </ToggleButton>
              </ToggleButtonGroup>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
      <Card>
        <TableContainer
          sx={{
            margin: 'auto',
            width: '100%',
            // paddingY: isUnlocked ? 0 : 2,
            // overflowX: 'hidden',
            tableLayout: 'fixed',
          }}
        >
          <Table aria-label="life-insurance-table">
            <TableBody
              sx={{
                '&:last-of-type > *': {
                  borderBottom: 'none',
                },
              }}
            >
              <HeaderRow />
              {allRows.map((row, index) => (
                <React.Fragment key={index}>
                  <InsuranceRow
                    row={row}
                    index={index}
                    isActive={isActive(index)}
                    hoveredRow={hoveredRow}
                    setHoveredRow={setHoveredRow}
                  />
                </React.Fragment>
              ))}
              {/* {allRows.map((row, index) => (
                <React.Fragment key={index}>
                  <InsuranceRow
                    row={row}
                    index={index}
                    isActive={isActive(index)}
                    hoveredRow={hoveredRow}
                    setHoveredRow={setHoveredRow}
                  />
                </React.Fragment>
              ))} */}
            </TableBody>
          </Table>
        </TableContainer>
        {/* <TableContainer
          sx={{
            margin: 'auto',
            width: '100%',
            paddingY: 2,
          }}
        >
          <Table aria-label="life-insurance-table">
            <TableBody
              sx={{
                '&:last-of-type > *': {
                  borderBottom: 'none',
                },
              }}
            >
              <HeaderRow />
              {privateRows.map((row, index) => (
                <React.Fragment key={index}>
                  <InsuranceRow
                    row={row}
                    index={index}
                    isActive={isActive(index)}
                    hoveredRow={hoveredRow}
                    setHoveredRow={setHoveredRow}
                  />
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer> */}
      </Card>
    </motion.div>
  )
}

export default LifeInsuranceSummary
